import { useState } from 'react'
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage'
import { useAuth } from '../../auth/AuthContext'

const API_BASE = import.meta.env.DEV ? '/api' : 'https://asia-northeast1-bluepaper2.cloudfunctions.net/api'

/** 設定＞個人設定＞プロフィール（表示名・アバター画像 + アカウント削除） */
export function ProfileSection() {
  const { user, firebaseUser, refreshUser, signOut } = useAuth()
  const [deleting, setDeleting] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [confirmText, setConfirmText] = useState('')
  const [deleteError, setDeleteError] = useState<string | null>(null)

  const handleDeleteAccount = async () => {
    if (!firebaseUser) return
    setDeleting(true)
    setDeleteError(null)
    try {
      const token = await firebaseUser.getIdToken()
      const res = await fetch(`${API_BASE}/v1/user/account`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      })
      if (!res.ok) {
        const data = await res.json().catch(() => ({}))
        throw new Error(data.error || `削除に失敗しました (${res.status})`)
      }
      // 削除完了 → サインアウトしてログイン画面へ
      await signOut()
      window.location.href = '/login'
    } catch (e) {
      setDeleteError(e instanceof Error ? e.message : '削除に失敗しました')
      setDeleting(false)
    }
  }
  const [displayName, setDisplayName] = useState(firebaseUser?.displayName || user?.displayName || '')
  const [photoURL, setPhotoURL] = useState(firebaseUser?.photoURL || '')
  const [imgError, setImgError] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [message, setMessage] = useState<string | null>(null)

  const handleAvatarFile = async (file: File | undefined) => {
    if (!file || !firebaseUser) return
    setError(null)
    if (!file.type.startsWith('image/')) {
      setError('画像ファイルを選択してください')
      return
    }
    if (file.size > 5 * 1024 * 1024) {
      setError('画像は5MB以下にしてください')
      return
    }
    setUploading(true)
    try {
      const storage = getStorage()
      const path = `avatars/${firebaseUser.uid}/${Date.now()}_${file.name}`
      const storageRef = ref(storage, path)
      await uploadBytes(storageRef, file)
      setPhotoURL(await getDownloadURL(storageRef))
      setImgError(false)
    } catch (e) {
      setError(e instanceof Error ? e.message : '画像のアップロードに失敗しました')
    } finally {
      setUploading(false)
    }
  }

  const handleSave = async () => {
    if (!firebaseUser) return
    if (!displayName.trim()) {
      setError('表示名を入力してください')
      return
    }
    setSaving(true)
    setError(null)
    setMessage(null)
    try {
      const token = await firebaseUser.getIdToken()
      const res = await fetch(`${API_BASE}/v1/user/profile`, {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          displayName: displayName.trim(),
          photoURL: photoURL || ''
        })
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok) throw new Error(data.error || `保存に失敗しました (${res.status})`)
      await refreshUser()
      setMessage('プロフィールを保存しました')
      setTimeout(() => setMessage(null), 3000)
    } catch (e) {
      setError(e instanceof Error ? e.message : '保存に失敗しました')
    } finally {
      setSaving(false)
    }
  }

  const initial = (displayName || user?.email || 'U').charAt(0).toUpperCase()

  return (
    <div className="card" style={{ maxWidth: 560 }}>
      <div className="card-header">
        <div>
          <h2 className="text-lg font-semibold">プロフィール</h2>
          <p className="form-section-desc">表示名とアバター画像を設定します（組織内のメンバー一覧や議事録に表示されます）</p>
        </div>
      </div>

      {error && <div className="alert danger" style={{ marginBottom: 12 }}>{error}</div>}
      {message && <div className="alert success" style={{ marginBottom: 12 }}>{message}</div>}

      <div className="flex items-center gap-4" style={{ marginBottom: 16 }}>
        {photoURL && !imgError ? (
          <img
            src={photoURL}
            alt="アバター"
            style={{ width: 64, height: 64, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }}
            onError={() => setImgError(true)}
          />
        ) : (
          <span
            className="avatar avatar-accent"
            style={{ width: 64, height: 64, fontSize: 24, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
          >
            {initial}
          </span>
        )}
        <div className="flex flex-col gap-2">
          <label className="btn btn-outline btn-sm" style={{ cursor: 'pointer', width: 'fit-content' }}>
            {uploading ? 'アップロード中...' : '画像を変更'}
            <input
              type="file"
              accept="image/*"
              style={{ display: 'none' }}
              disabled={uploading}
              onChange={(e) => { handleAvatarFile(e.target.files?.[0]); e.target.value = '' }}
            />
          </label>
          {photoURL && (
            <button className="btn btn-ghost btn-sm" style={{ width: 'fit-content' }} onClick={() => setPhotoURL('')}>
              画像を削除
            </button>
          )}
        </div>
      </div>

      <div className="form-group">
        <label className="form-label">表示名</label>
        <input
          type="text"
          className="form-input"
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
          placeholder="例: 大江 隆允"
          maxLength={50}
        />
      </div>

      <div className="form-group">
        <label className="form-label">メールアドレス</label>
        <input type="text" className="form-input" value={user?.email || ''} readOnly disabled />
        <p className="form-hint">メールアドレスはGoogleアカウントに紐づいているため変更できません。</p>
      </div>

      <div className="flex justify-end">
        <button className="btn btn-primary" disabled={saving || uploading} onClick={handleSave}>
          {saving && <span className="spinner sm" />}
          保存
        </button>
      </div>

      {/* アカウント削除（App Store審査要件: アプリ内で退会できること） */}
      <div style={{ marginTop: 32, paddingTop: 24, borderTop: '1px solid var(--color-border-light)' }}>
        <h3 className="text-md font-semibold" style={{ color: 'var(--color-danger, #dc2626)' }}>アカウントを削除</h3>
        <p className="form-section-desc" style={{ marginTop: 4 }}>
          アカウントと個人情報（プロフィール・組織メンバー情報）を完全に削除します。この操作は取り消せません。
        </p>

        {deleteError && <div className="alert danger" style={{ marginTop: 12 }}>{deleteError}</div>}

        {!showDeleteConfirm ? (
          <button
            className="btn btn-outline btn-sm"
            style={{ marginTop: 12, color: 'var(--color-danger, #dc2626)', borderColor: 'var(--color-danger, #dc2626)' }}
            onClick={() => { setShowDeleteConfirm(true); setDeleteError(null) }}
          >
            アカウントを削除する
          </button>
        ) : (
          <div style={{ marginTop: 12 }}>
            <p className="text-sm" style={{ marginBottom: 8 }}>
              確認のため <strong>削除</strong> と入力してください。
            </p>
            <input
              type="text"
              className="form-input"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder="削除"
              style={{ maxWidth: 240 }}
            />
            <div className="flex gap-2" style={{ marginTop: 12 }}>
              <button
                className="btn btn-sm"
                style={{ background: 'var(--color-danger, #dc2626)', color: '#fff' }}
                disabled={confirmText.trim() !== '削除' || deleting}
                onClick={handleDeleteAccount}
              >
                {deleting && <span className="spinner sm" />}
                完全に削除
              </button>
              <button
                className="btn btn-ghost btn-sm"
                disabled={deleting}
                onClick={() => { setShowDeleteConfirm(false); setConfirmText('') }}
              >
                キャンセル
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
